import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { fetchData, deleteData, updateData } from "../../services/apiServices";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import classnames from "classnames";

const SubCategory = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [error, setError] = useState("");
  const [isReordering, setIsReordering] = useState(false);
  const [openAccordion, setOpenAccordion] = useState("");

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? "" : id);
  };

  const handleDelete = async (id) => {
    console.log("id :>> ", id);
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      try {
        await deleteData(`api/admin/deletesubcategories/${id}`);
        toast.success("Subcategory deleted successfully");
        getData(); // Refresh the list
      } catch (error) {
        toast.error("Failed to delete subcategory");
      }
    }
  };

  // Bulk update subcategories order within a category
  const handleBulkOrderUpdate = async (updatedSubcategories, categoryId) => {
    setIsReordering(true);
    try {
      const updatePromises = updatedSubcategories.map((subcategory, index) =>
        updateData(`subcategories/${subcategory._id}`, { order: index + 1 })
      );

      await Promise.all(updatePromises);
      toast.success("Subcategories reordered successfully");
      getData(); // Refresh data
    } catch (error) {
      console.error("Failed to update order:", error);
      toast.error("Failed to update subcategories order");
    } finally {
      setIsReordering(false);
    }
  };

  // Handle drag end for subcategories within a category
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceDroppableId = result.source.droppableId;
    const destinationDroppableId = result.destination.droppableId;

    // Only allow reordering within the same category
    if (sourceDroppableId !== destinationDroppableId) {
      toast.warning("Cannot move subcategories between different categories");
      return;
    }

    const categoryId = sourceDroppableId.replace("subcategories-", "");

    // Update the local state
    const updatedCategoriesData = categoriesData.map((category) => {
      if (category._id === categoryId) {
        const items = Array.from(category.subcategories);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        return { ...category, subcategories: items };
      }
      return category;
    });

    setCategoriesData(updatedCategoriesData);

    // Find the updated subcategories for this category
    const updatedCategory = updatedCategoriesData.find(
      (cat) => cat._id === categoryId
    );
    if (updatedCategory) {
      handleBulkOrderUpdate(updatedCategory.subcategories, categoryId);
    }
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Order</span>,
      selector: (row) => row.order || 0,
      sortable: true,
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      cell: (row) => (
        <img
          src={row.image}
          alt={row.name}
          style={{ width: "50px", height: "50px", objectFit: "cover" }}
        />
      ),
      width: "100px",
    },
    {
      name: <span className="font-weight-bold fs-13">Name</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Description</span>,
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Category</span>,
      selector: (row) => row.categoryName,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Created At</span>,
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Actions</span>,
      cell: (row) => (
        <div>
          <Link
            to={`/sub-categories/edit/${row._id}`}
            className="btn btn-primary btn-sm me-2"
          >
            Edit
          </Link>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row._id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Transform the fetched data into the required format
  const transformData = (categories) => {
    const transformedData = [];

    categories.forEach((category) => {
      if (category.subcategories && category.subcategories.length > 0) {
        category.subcategories.forEach((subcategory) => {
          transformedData.push({
            _id: subcategory._id,
            name: subcategory.name,
            image: subcategory.image,
            description: subcategory.description,
            categoryName: category.name,
            categoryId: category._id,
            createdAt: subcategory.createdAt,
            order: subcategory.order || 0,
          });
        });
      }
    });

    return transformedData;
  };

  const getData = async () => {
    try {
      const response = await fetchData("api/admin/categories");
      console.log("API Response:", response); // Debug log

      if (
        response &&
        response.status &&
        response.data &&
        response.data.categories
      ) {
        // Sort categories by order
        const sortedCategories = response.data.categories.sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );

        // Sort subcategories within each category by order
        const processedCategories = sortedCategories.map((category) => ({
          ...category,
          subcategories: (category.subcategories || []).sort(
            (a, b) => (a.order || 0) - (b.order || 0)
          ),
        }));

        setCategoriesData(processedCategories);

        // Transform into row-friendly format
        const transformedData = transformData(processedCategories);
        setRows(transformedData);
      } else {
        throw new Error("Invalid response format from API");
      }

      setPending(false);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setError(error.message);
      toast.error("Failed to load subcategories");
      setPending(false);
    }
  };

  useEffect(() => {
    getData();
    const timeout = setTimeout(() => {
      setPending(false);
    }, 2000);
    return () => clearTimeout(timeout);
  }, []);

  // Drag and drop item component for subcategories
  const DragDropSubcategoryItem = ({ subcategory, index }) => (
    <Draggable
      key={subcategory._id}
      draggableId={subcategory._id}
      index={index}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`card mb-2 ${snapshot.isDragging ? "shadow-lg" : ""}`}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <div className="card-body p-3">
            <Row className="align-items-center">
              <Col xs="1">
                <div className="text-muted">
                  <i className="mdi mdi-drag-horizontal fs-16"></i>
                </div>
              </Col>
              <Col xs="1">
                <span className="badge bg-secondary">
                  {subcategory.order || index + 1}
                </span>
              </Col>
              <Col xs="2">
                <img
                  src={subcategory.image}
                  alt={subcategory.name}
                  style={{ width: "40px", height: "40px", objectFit: "cover" }}
                  className="rounded"
                />
              </Col>
              <Col xs="3">
                <h6 className="mb-1">{subcategory.name}</h6>
              </Col>
              <Col xs="3">
                <p className="text-muted mb-0 small">
                  {subcategory.description}
                </p>
              </Col>
              <Col xs="2">
                <div className="d-flex gap-1">
                  <Link
                    to={`/sub-categories/edit/${subcategory._id}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="mdi mdi-pencil"></i>
                  </Link>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(subcategory._id)}
                  >
                    <i className="mdi mdi-delete"></i>
                  </button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <div>
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => toggleTab("1")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-table me-2"></i>
                    Table View
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => toggleTab("2")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="mdi mdi-drag me-2"></i>
                    Reorder Subcategories
                  </NavLink>
                </NavItem>
              </Nav>
            </Col>
          </Row>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <DataTable
                columns={columns}
                pagination
                data={rows}
                progressPending={pending}
                defaultSortFieldId={1}
                defaultSortAsc={true}
              />
            </TabPane>

            <TabPane tabId="2">
              <Row className="mb-3">
                <Col>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Reorder Subcategories by Category</h5>
                    {isReordering && (
                      <div className="d-flex align-items-center">
                        <Spinner size="sm" className="me-2" />
                        <span>Updating order...</span>
                      </div>
                    )}
                  </div>
                  <p className="text-muted small">
                    Drag subcategories up or down within each category to change
                    their display order. Changes are saved automatically.
                  </p>
                </Col>
              </Row>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Accordion open={openAccordion} toggle={toggleAccordion}>
                  {categoriesData.map((category) => (
                    <AccordionItem key={category._id}>
                      <AccordionHeader targetId={category._id}>
                        <div className="d-flex align-items-center">
                          <img
                            src={category.image}
                            alt={category.name}
                            style={{
                              width: "30px",
                              height: "30px",
                              objectFit: "cover",
                            }}
                            className="rounded me-3"
                          />
                          <div>
                            <h6 className="mb-0">{category.name}</h6>
                            <small className="text-muted">
                              {category.subcategories?.length || 0}{" "}
                              subcategories
                            </small>
                          </div>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId={category._id}>
                        {category.subcategories &&
                        category.subcategories.length > 0 ? (
                          <Droppable
                            droppableId={`subcategories-${category._id}`}
                          >
                            {(provided, snapshot) => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className={`${
                                  snapshot.isDraggingOver ? "bg-light" : ""
                                } p-2 rounded`}
                                style={{ minHeight: "100px" }}
                              >
                                {category.subcategories.map(
                                  (subcategory, index) => (
                                    <DragDropSubcategoryItem
                                      key={subcategory._id}
                                      subcategory={subcategory}
                                      index={index}
                                    />
                                  )
                                )}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            <i className="mdi mdi-folder-open-outline fs-48"></i>
                            <p>No subcategories found in this category</p>
                          </div>
                        )}
                      </AccordionBody>
                    </AccordionItem>
                  ))}
                </Accordion>
              </DragDropContext>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default SubCategory;
